import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { TrendingUp, DollarSign, Package, Download, Calculator, Target, AlertTriangle, BarChart3 } from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { cogsCalculatorService } from '../../services/cogsCalculatorService';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currencyFormatter';

const COGSPercentageCalculator = () => {
  const { profile } = useAuth();
  const [materialCosts, setMaterialCosts] = useState('');
  const [laborCosts, setLaborCosts] = useState('');
  const [overheadCosts, setOverheadCosts] = useState('');
  const [shippingCosts, setShippingCosts] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [targetMargin, setTargetMargin] = useState('30');
  const [result, setResult] = useState(null);
  const [batchData, setBatchData] = useState([]);
  const [activeTab, setActiveTab] = useState('calculator'); // 'calculator' or 'batch'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const formatCurrency = (amount) => {
    const currency = profile?.currency || 'ZAR';
    return formatCurrencyUtil(parseNum(amount), currency);
  };

  const formatPercent = (value) => {
    return `${parseNum(value)?.toFixed(2)}%`;
  };

  // Load saved calculations from Supabase
  useEffect(() => {
    const loadCalculations = async () => {
      try {
        setLoading(true);
        setError('');
        const calculations = await cogsCalculatorService?.getAll();
        setBatchData(calculations?.map(calc => ({
          id: calc?.id,
          timestamp: new Date(calc?.createdAt)?.toLocaleTimeString(),
          category: calc?.calculationPeriod || 'Uncategorized',
          materialCosts: 0, // Not stored separately in schema
          laborCosts: 0,
          overheadCosts: 0,
          shippingCosts: 0,
          totalCOGS: calc?.costOfGoodsSold,
          sellingPrice: calc?.revenue,
          cogsPercentage: calc?.cogsPercentage,
          grossMargin: calc?.grossMarginPercentage,
          grossProfit: calc?.grossProfit,
          markupPercentage: calc?.costOfGoodsSold > 0 ? ((calc?.revenue - calc?.costOfGoodsSold) / calc?.costOfGoodsSold) * 100 : 0,
          recommendedPrice: calc?.costOfGoodsSold / (1 - 30 / 100),
          minimumPrice: calc?.costOfGoodsSold / (1 - 0.15),
          breakEvenPrice: calc?.costOfGoodsSold,
          targetMargin: 30,
          priceGap: calc?.revenue > 0 ? (calc?.costOfGoodsSold / (1 - 30 / 100)) - calc?.revenue : 0,
        })) || []);
      } catch (err) {
        setError(err?.message || 'Failed to load calculations');
      } finally {
        setLoading(false);
      }
    };

    loadCalculations();
  }, []);

  const handleCalculate = () => {
    const materials = parseNum(materialCosts);
    const labor = parseNum(laborCosts);
    const overhead = parseNum(overheadCosts);
    const shipping = parseNum(shippingCosts);
    const price = parseNum(sellingPrice);
    const target = parseNum(targetMargin);

    const totalCOGS = materials + labor + overhead + shipping;
    
    if (totalCOGS <= 0) return;

    const cogsPercentage = price > 0 ? (totalCOGS / price) * 100 : 0;
    const grossMargin = price > 0 ? ((price - totalCOGS) / price) * 100 : 0;
    const grossProfit = price - totalCOGS;
    const markupPercentage = totalCOGS > 0 ? ((price - totalCOGS) / totalCOGS) * 100 : 0;
    
    // Recommended pricing based on target margin
    const recommendedPrice = totalCOGS / (1 - target / 100);
    const minimumPrice = totalCOGS / (1 - 0.15); // 15% minimum margin

    // Break-even analysis
    const breakEvenPrice = totalCOGS;

    const calculationResult = {
      id: Date.now(),
      timestamp: new Date()?.toLocaleTimeString(),
      category: productCategory || 'Uncategorized',
      materialCosts: materials,
      laborCosts: labor,
      overheadCosts: overhead,
      shippingCosts: shipping,
      totalCOGS,
      sellingPrice: price,
      cogsPercentage,
      grossMargin,
      grossProfit,
      markupPercentage,
      recommendedPrice,
      minimumPrice,
      breakEvenPrice,
      targetMargin: target,
      priceGap: price > 0 ? recommendedPrice - price : 0,
    };

    setResult(calculationResult);
    setSaveSuccess(false);
  };

  const addToBatch = async () => {
    if (!result) return;
    
    try {
      setSaveSuccess(false);
      setError('');

      // Save to Supabase
      const savedCalculation = await cogsCalculatorService?.create({
        revenue: result?.sellingPrice,
        costOfGoodsSold: result?.totalCOGS,
        cogsPercentage: result?.cogsPercentage,
        grossProfit: result?.grossProfit,
        grossMarginPercentage: result?.grossMargin,
        calculationPeriod: result?.category || 'Uncategorized',
        periodStartDate: null,
        periodEndDate: null,
      });

      // Update local state with saved calculation
      const newBatchItem = {
        ...result,
        id: savedCalculation?.id,
        timestamp: new Date(savedCalculation?.createdAt)?.toLocaleTimeString(),
      };

      setBatchData(prev => [newBatchItem, ...prev]?.slice(0, 20));
      setSaveSuccess(true);
      
      // Navigate to batch analysis tab
      setActiveTab('batch');
    } catch (err) {
      setError(err?.message || 'Failed to save calculation');
    }
  };

  const clearBatch = async () => {
    try {
      setError('');
      await cogsCalculatorService?.deleteAll();
      setBatchData([]);
    } catch (err) {
      setError(err?.message || 'Failed to clear calculations');
    }
  };

  const exportReport = () => {
    const reportData = {
      calculation: result,
      batchAnalysis: batchData,
      timestamp: new Date()?.toISOString(),
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL?.createObjectURL(blob);
    const link = document?.createElement('a');
    link.href = url;
    link.download = `cogs-analysis-report-${Date.now()}.json`;
    link?.click();
  };

  const getMarginColor = (margin) => {
    if (margin >= 40) return { text: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' };
    if (margin >= 25) return { text: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' };
    if (margin >= 15) return { text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' };
    return { text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' };
  };

  const costBreakdownData = result ? [
    { name: 'Materials', value: result?.materialCosts, color: '#3b82f6' },
    { name: 'Labor', value: result?.laborCosts, color: '#8b5cf6' },
    { name: 'Overhead', value: result?.overheadCosts, color: '#f59e0b' },
    { name: 'Shipping', value: result?.shippingCosts, color: '#10b981' },
  ]?.filter(item => item?.value > 0) : [];

  const comparisonData = result ? [
    { name: 'Current', price: result?.sellingPrice, cogs: result?.totalCOGS, profit: result?.grossProfit },
    { name: 'Recommended', price: result?.recommendedPrice, cogs: result?.totalCOGS, profit: result?.recommendedPrice - result?.totalCOGS },
    { name: 'Minimum', price: result?.minimumPrice, cogs: result?.totalCOGS, profit: result?.minimumPrice - result?.totalCOGS },
  ] : [];

  const avgCOGS = batchData?.length > 0 
    ? batchData?.reduce((sum, item) => sum + item?.cogsPercentage, 0) / batchData?.length 
    : 0;

  const avgMargin = batchData?.length > 0 
    ? batchData?.reduce((sum, item) => sum + item?.grossMargin, 0) / batchData?.length 
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar />
        <Header />
        <main className="ml-64 mt-16 p-6">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading calculations...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <Header />
      <main className="ml-64 mt-16 p-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <Package className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">COGS Percentage Calculator</h1>
                <p className="text-sm text-slate-600">Comprehensive cost analysis and pricing optimization</p>
              </div>
            </div>
            {result && (
              <button
                onClick={exportReport}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export Analysis
              </button>
            )}
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {saveSuccess && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">Calculation saved successfully!</p>
            </div>
          )}

          {/* Tabs Navigation */}
          <div className="flex gap-2 mt-6 border-b border-slate-200">
            <button
              onClick={() => setActiveTab('calculator')}
              className={`px-6 py-3 font-medium transition-all relative ${
                activeTab === 'calculator' ?'text-indigo-600 border-b-2 border-indigo-600' :'text-slate-600 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                Calculator
              </div>
            </button>
            <button
              onClick={() => setActiveTab('batch')}
              className={`px-6 py-3 font-medium transition-all relative ${
                activeTab === 'batch' ?'text-indigo-600 border-b-2 border-indigo-600' :'text-slate-600 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Batch Analysis
                {batchData?.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs font-bold bg-indigo-100 text-indigo-700 rounded-full">
                    {batchData?.length}
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Calculator Tab */}
        {activeTab === 'calculator' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Panel - Cost Inputs */}
            <div className="lg:col-span-5">
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-6">Cost Components</h3>

                <div className="space-y-4">
                  {/* Product Category */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Product Category (Optional)</label>
                    <input
                      type="text"
                      value={productCategory}
                      onChange={(e) => setProductCategory(e?.target?.value)}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      placeholder="e.g., Electronics, Apparel"
                    />
                  </div>

                  {/* Material Costs */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-blue-600" />
                        Direct Material Costs
                      </div>
                    </label>
                    <input
                      type="text"
                      value={materialCosts}
                      onChange={(e) => setMaterialCosts(e?.target?.value)}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      placeholder="Enter material costs"
                    />
                  </div>

                  {/* Labor Costs */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-purple-600" />
                        Labor Expenses
                      </div>
                    </label>
                    <input
                      type="text"
                      value={laborCosts}
                      onChange={(e) => setLaborCosts(e?.target?.value)}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      placeholder="Enter labor costs"
                    />
                  </div>

                  {/* Overhead Costs */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-orange-600" />
                        Overhead Allocation
                      </div>
                    </label>
                    <input
                      type="text"
                      value={overheadCosts}
                      onChange={(e) => setOverheadCosts(e?.target?.value)}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      placeholder="Enter overhead costs"
                    />
                  </div>

                  {/* Shipping Costs */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-green-600" />
                        Shipping & Handling
                      </div>
                    </label>
                    <input
                      type="text"
                      value={shippingCosts}
                      onChange={(e) => setShippingCosts(e?.target?.value)}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      placeholder="Enter shipping costs"
                    />
                  </div>

                  {/* Selling Price */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Current Selling Price</label>
                    <input
                      type="text"
                      value={sellingPrice}
                      onChange={(e) => setSellingPrice(e?.target?.value)}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      placeholder="Enter selling price"
                    />
                  </div>

                  {/* Target Margin */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Target Gross Margin (%)
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={targetMargin}
                        onChange={(e) => setTargetMargin(e?.target?.value)}
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all pr-10"
                        placeholder="Enter target margin"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">%</span>
                    </div>
                  </div>

                  {/* Calculate Button */}
                  <button
                    onClick={handleCalculate}
                    className="w-full px-6 py-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    <Calculator className="w-5 h-5" />
                    Calculate COGS & Margins
                  </button>

                  {result && (
                    <button
                      onClick={addToBatch}
                      className="w-full px-6 py-3 rounded-lg bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-medium transition-colors"
                    >
                      Add to Batch Analysis
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Center - Results & Analysis */}
            <div className="lg:col-span-7">
              {result && (
                <>
                  {/* Key Metrics */}
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-4">Profitability Metrics</h3>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <span className="text-sm font-medium text-blue-900 block mb-1">Total COGS</span>
                        <p className="text-2xl font-bold text-blue-700">{formatCurrency(result?.totalCOGS)}</p>
                      </div>
                      <div className={`p-4 rounded-lg border ${getMarginColor(result?.grossMargin)?.bg} ${getMarginColor(result?.grossMargin)?.border}`}>
                        <span className={`text-sm font-medium block mb-1 ${getMarginColor(result?.grossMargin)?.text}`}>Gross Margin</span>
                        <p className={`text-2xl font-bold ${getMarginColor(result?.grossMargin)?.text}`}>{formatPercent(result?.grossMargin)}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <span className="text-sm font-medium text-purple-900 block mb-1">COGS Percentage</span>
                        <p className="text-2xl font-bold text-purple-700">{formatPercent(result?.cogsPercentage)}</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <span className="text-sm font-medium text-green-900 block mb-1">Gross Profit</span>
                        <p className="text-2xl font-bold text-green-700">{formatCurrency(result?.grossProfit)}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <span className="text-sm font-medium text-slate-700 block mb-1">Markup Percentage</span>
                        <p className="text-xl font-bold text-slate-900">{formatPercent(result?.markupPercentage)}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <span className="text-sm font-medium text-slate-700 block mb-1">Break-Even Price</span>
                        <p className="text-xl font-bold text-slate-900">{formatCurrency(result?.breakEvenPrice)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Recommendations */}
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-indigo-600" />
                      Pricing Recommendations
                    </h3>

                    <div className="space-y-4">
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="text-sm font-medium text-green-900 block">Recommended Price</span>
                            <span className="text-xs text-green-700">Based on {targetMargin}% target margin</span>
                          </div>
                          <p className="text-2xl font-bold text-green-700">{formatCurrency(result?.recommendedPrice)}</p>
                        </div>
                        {result?.priceGap !== 0 && (
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-green-200">
                            <AlertTriangle className="w-4 h-4 text-green-700" />
                            <span className="text-xs text-green-700">
                              {result?.priceGap > 0 
                                ? `Increase price by ${formatCurrency(result?.priceGap)} to hit target` 
                                : `You're ${formatCurrency(Math.abs(result?.priceGap))} above target`}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="text-sm font-medium text-orange-900 block">Minimum Viable Price</span>
                            <span className="text-xs text-orange-700">15% minimum margin for sustainability</span>
                          </div>
                          <p className="text-xl font-bold text-orange-700">{formatCurrency(result?.minimumPrice)}</p>
                        </div>
                      </div>

                      {result?.sellingPrice < result?.minimumPrice && (
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                            <span className="text-sm font-medium text-red-900">Warning: Price below minimum threshold</span>
                          </div>
                          <p className="text-xs text-red-700 mt-2">
                            Current price may not be sustainable. Consider increasing to at least {formatCurrency(result?.minimumPrice)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Cost Breakdown Chart */}
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-4">Cost Breakdown Analysis</h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPie>
                            <Pie
                              data={costBreakdownData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={(entry) => `${entry?.name}: ${formatPercent((entry?.value / result?.totalCOGS) * 100)}`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {costBreakdownData?.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry?.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </RechartsPie>
                        </ResponsiveContainer>
                      </div>
                      <div className="space-y-3">
                        {costBreakdownData?.map((item) => (
                          <div key={item?.name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 rounded" style={{ backgroundColor: item?.color }} />
                              <span className="text-sm font-medium text-slate-700">{item?.name}</span>
                            </div>
                            <span className="text-sm font-bold text-slate-900">{formatCurrency(item?.value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Price Comparison Chart */}
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-4">Pricing Strategy Comparison</h3>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={comparisonData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Bar dataKey="price" fill="#6366f1" name="Selling Price" />
                          <Bar dataKey="cogs" fill="#f59e0b" name="COGS" />
                          <Bar dataKey="profit" fill="#10b981" name="Gross Profit" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              )}

              {!result && (
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 mb-2">No Analysis Yet</h3>
                  <p className="text-sm text-slate-600">Enter your cost components to calculate COGS and profitability metrics</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Batch Analysis Tab */}
        {activeTab === 'batch' && (
          <div className="space-y-6">
            {batchData?.length > 0 ? (
              <>
                {/* Batch Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Package className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Products</p>
                        <p className="text-2xl font-bold text-slate-900">{batchData?.length}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Avg COGS %</p>
                        <p className="text-2xl font-bold text-slate-900">{formatPercent(avgCOGS)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Target className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Avg Margin</p>
                        <p className="text-2xl font-bold text-slate-900">{formatPercent(avgMargin)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Batch Analysis Table */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                  <div className="p-6 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-slate-900">Portfolio Analysis</h3>
                      <button
                        onClick={clearBatch}
                        className="px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg font-medium transition-all"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="space-y-4">
                      {batchData?.map((item, index) => (
                        <div key={item?.id} className="p-4 border border-slate-200 rounded-lg hover:border-indigo-300 hover:shadow-md transition-all">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="text-base font-bold text-slate-900">{item?.category}</h4>
                              <span className="text-xs text-slate-500">{item?.timestamp}</span>
                            </div>
                            <span className={`text-sm font-bold px-3 py-1 rounded-full ${getMarginColor(item?.grossMargin)?.bg} ${getMarginColor(item?.grossMargin)?.text}`}>
                              {formatPercent(item?.grossMargin)} Margin
                            </span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                            <div className="p-3 bg-slate-50 rounded-lg">
                              <span className="text-xs text-slate-600 block mb-1">Total COGS</span>
                              <span className="text-sm font-bold text-slate-900">{formatCurrency(item?.totalCOGS)}</span>
                            </div>
                            <div className="p-3 bg-slate-50 rounded-lg">
                              <span className="text-xs text-slate-600 block mb-1">Selling Price</span>
                              <span className="text-sm font-bold text-slate-900">{formatCurrency(item?.sellingPrice)}</span>
                            </div>
                            <div className="p-3 bg-green-50 rounded-lg">
                              <span className="text-xs text-green-700 block mb-1">Gross Profit</span>
                              <span className="text-sm font-bold text-green-700">{formatCurrency(item?.grossProfit)}</span>
                            </div>
                            <div className="p-3 bg-purple-50 rounded-lg">
                              <span className="text-xs text-purple-700 block mb-1">COGS %</span>
                              <span className="text-sm font-bold text-purple-700">{formatPercent(item?.cogsPercentage)}</span>
                            </div>
                          </div>

                          <div className="grid grid-cols-4 gap-3 mt-3 pt-3 border-t border-slate-200">
                            <div>
                              <span className="text-xs text-slate-600 block">Materials</span>
                              <span className="text-xs font-medium text-slate-900">{formatCurrency(item?.materialCosts)}</span>
                            </div>
                            <div>
                              <span className="text-xs text-slate-600 block">Labor</span>
                              <span className="text-xs font-medium text-slate-900">{formatCurrency(item?.laborCosts)}</span>
                            </div>
                            <div>
                              <span className="text-xs text-slate-600 block">Overhead</span>
                              <span className="text-xs font-medium text-slate-900">{formatCurrency(item?.overheadCosts)}</span>
                            </div>
                            <div>
                              <span className="text-xs text-slate-600 block">Shipping</span>
                              <span className="text-xs font-medium text-slate-900">{formatCurrency(item?.shippingCosts)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
                <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-900 mb-2">No Batch Data Yet</h3>
                <p className="text-sm text-slate-600 mb-6">
                  Calculate products and add them to batch analysis to view portfolio insights
                </p>
                <button
                  onClick={() => setActiveTab('calculator')}
                  className="px-6 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-colors inline-flex items-center gap-2"
                >
                  <Calculator className="w-4 h-4" />
                  Go to Calculator
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default COGSPercentageCalculator;